import {useTranslation} from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {Box, Grid, IconButton, TextField, Typography, useTheme} from '@mui/material';

import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import type {WorkforceProfileExtractedSkill} from '../../types/workforceProfileDetailsTypes';

export interface Props {
  title: string;
  rows: WorkforceProfileExtractedSkill[];
  suggested?: boolean;
  onChange: (rows: WorkforceProfileExtractedSkill[]) => void;
}

export const WorkforceProfileWizardSkillsEditor = ({title, rows, suggested, onChange}: Props) => {
  const theme = useTheme();
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.wizard.skillsEditor'});

  const updateRow = (index: number, patch: Partial<WorkforceProfileExtractedSkill>) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? {...row, ...patch} : row)));
  };

  const addRow = () => {
    onChange([...rows, {skill: '', evidence: '', confidence: 'medium'}]);
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  return (
    <Box>
      <Typography variant='subtitle2' sx={{mb: 1.5, fontWeight: 600}}>
        {title}
        {suggested ? ` (${t('suggested')})` : ''}
      </Typography>
      {rows.map((row, index) => (
        <Grid container spacing={1.5} key={`skill-row-${index}`} sx={{mb: 1.5}} alignItems='flex-start'>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size='small'
              label={t('skill')}
              value={row.skill}
              onChange={(event) => updateRow(index, {skill: event.target.value})}
              sx={getFieldStyle(theme)}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              size='small'
              label={t('evidence')}
              value={row.evidence}
              onChange={(event) => updateRow(index, {evidence: event.target.value})}
              sx={getFieldStyle(theme)}
            />
          </Grid>
          <Grid item xs={12} sm={1} sx={{display: 'flex', justifyContent: {xs: 'flex-end', sm: 'center'}}}>
            <IconButton aria-label={String(t('remove'))} onClick={() => removeRow(index)} size='small'>
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <EmpionButton variant='secondary' startIcon={<AddIcon />} onClick={addRow}>
        {t('add')}
      </EmpionButton>
    </Box>
  );
};
